/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br."Knowledge
 * for Decisions (K4D)" and the slogan"Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { useCallback, useEffect, useRef } from"react";
import { createPortal } from"react-dom";
import { useMediaQuery } from"@/shared/hooks/use-media-query";
import { SelectErrorMessages } from"./Select.parts";
import { cn, useClickOutsideClose } from"./Select.helpers";
import { useDropdownPosition } from"./use-dropdown-position";
import {
  TriggerButton, OptionsList, SearchInput, renderDesktopDropdown,
} from"./Select.trigger-parts";

interface TriggerModeProps {
  state: any;
  refs: any;
  name: string;
  labelText: string;
  required: boolean;
  disabled: boolean;
  multiple: boolean;
  searchable: boolean;
  rawSearchable?: boolean;
  clearable: boolean;
  rawValue: string | null | undefined;
  rawPlaceholder?: string;
  singleValue: string;
  leftSection?: React.ReactNode;
  idProp?: string;
  classNameProp?: string;
  styleProp?: React.CSSProperties;
  restProps: Record<string, any>;
  submitted: boolean;
  toPresentErrors: boolean;
  allErrorMessages: string[];
  hasErrorBorder: boolean;
  renderOption?: (optionValue: string, selectedValue: string, isActive: boolean) => React.ReactNode;
  nothingFoundMessage?: string;
  handleSearchKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  t: (k: string, o?: any) => string;
}

const SelectTriggerMode: React.FC<TriggerModeProps> = (props) => {
  const {
    state, refs, name, labelText, required, disabled, multiple, searchable, rawSearchable,
    clearable, rawValue, rawPlaceholder, singleValue, leftSection, idProp, classNameProp,
    styleProp, restProps, submitted, toPresentErrors, allErrorMessages, hasErrorBorder,
    renderOption, nothingFoundMessage, handleSearchKeyDown, t,
  } = props;
  const { containerRef } = refs;
  const isMobile = useMediaQuery('(max-width: 767px)');
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useClickOutsideClose(state.isOpen && !isMobile, containerRef, state.close);

  useEffect(() => {
    if (!state.isOpen || !isMobile) return;
    document.body.style.overflow ="hidden";
    return () => { document.body.style.overflow =""; };
  }, [state.isOpen, isMobile]);

  useEffect(() => {
    if (state.activeOptionIndex >= 0 && optionRefs.current[state.activeOptionIndex]) {
      optionRefs.current[state.activeOptionIndex]?.scrollIntoView?.({ block:'nearest' });
    }
  }, [state.activeOptionIndex]);

  useEffect(() => {
    if (!state.isOpen) return;
    const timer = setTimeout(() => searchInputRef.current?.focus(), 50);
    return () => clearTimeout(timer);
  }, [state.isOpen]);

  const errorBorder = hasErrorBorder
    ?"border-[var(--status-error)] ring-1 ring-[var(--status-error)]"
    :"border-[var(--border-subtle)]";

  const handleClear = (e: React.MouseEvent) => { e.stopPropagation(); state.handleClear(); };

  const handleTriggerKeyDown = useCallback((e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (!state.isOpen) return;
    const data = state.filteredData;
    if (e.key ==='ArrowDown') {
      e.preventDefault();
      state.setActiveOptionIndex((prev: number) => (prev + 1 >= data.length ? 0 : prev + 1));
    } else if (e.key ==='ArrowUp') {
      e.preventDefault();
      state.setActiveOptionIndex((prev: number) => (prev - 1 < 0 ? data.length - 1 : prev - 1));
    } else if (e.key ==='Enter') {
      e.preventDefault();
      if (data.length > 0) {
        const idx = state.activeOptionIndex >= 0 && state.activeOptionIndex < data.length ? state.activeOptionIndex : 0;
        state.onSelect(data[idx].value);
      }
    } else if (e.key ==='Escape') {
      e.preventDefault();
      state.close();
    }
  }, [state.isOpen, state.filteredData, state.activeOptionIndex, state.setActiveOptionIndex, state.onSelect, state.close]);

  const searchInput = (
    <SearchInput state={state} isMobile={isMobile} searchInputRef={searchInputRef}
      handleSearchKeyDown={handleSearchKeyDown} t={t} />
  );

  const optionsList = (
    <OptionsList state={state} multiple={multiple} isMobile={isMobile} singleValue={singleValue}
      nothingFoundMessage={nothingFoundMessage} renderOption={renderOption} optionRefs={optionRefs} t={t} />
  );

  const pos = useDropdownPosition(containerRef, state.isOpen && !isMobile);

  const desktopDropdown = renderDesktopDropdown(state, isMobile, pos, searchable, searchInput, optionsList);

  const mobileSheet = state.isOpen && isMobile ? createPortal(
    <div className="fixed inset-0 z-[10050] flex flex-col justify-end pointer-events-auto"
      onClick={(e) => { if (e.target === e.currentTarget) state.close(); }} role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40" onClick={state.close} />
      <div className={cn("relative bg-[var(--surface-primary)] rounded-t-(--radius-lg) max-h-[70dvh] flex flex-col shadow-[var(--shadow-lg)] animate-[slideUp_200ms_ease-out]")}>
        <div className="flex items-center justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-[var(--border-medium)]" />
        </div>
        {labelText && (
          <div className="px-4 pb-2 pt-1">
            <span className="text-base font-medium text-[var(--text-primary)]">{labelText}</span>
          </div>
        )}
        {rawSearchable ? searchInput : null}
        <div className="flex-1 overflow-y-auto overscroll-contain">{optionsList}</div>
        <div className="p-3 border-t border-[var(--border-subtle)]">
          <button type="button" onClick={state.close}
            className={cn("w-full py-3 text-base font-medium rounded-(--radius-md) bg-[var(--surface-secondary)] text-[var(--text-primary)] active:bg-[var(--surface-hover)]")}>
            {t('buttons.close')}
          </button>
        </div>
      </div>
    </div>, document.body
  ) : null;

  return (
    <div className={cn("relative", classNameProp)} style={styleProp} ref={containerRef}>
      {labelText && (
        <label htmlFor={idProp || name}
          className={cn(
            'block mb-(--space-1)',
            'font-[family-name:var(--font-family)]',
            'font-[weight:var(--font-medium)]',
            'text-[length:var(--text-small)]',
            'leading-[var(--leading-snug)]',
            'tracking-[var(--tracking-wide)]',
            'text-(--text-tertiary)',
          )}
        >
          {labelText}{required && <span className="ml-(--space-1) text-(--status-error)">*</span>}
        </label>
      )}
      <div className="relative">
        <TriggerButton state={state} disabled={disabled} multiple={multiple} clearable={clearable}
          rawValue={rawValue} rawPlaceholder={rawPlaceholder} labelText={labelText} leftSection={leftSection}
          idProp={idProp} name={name} errorBorder={errorBorder} restProps={restProps}
          handleTriggerKeyDown={handleTriggerKeyDown} handleClear={handleClear} t={t} />
        {desktopDropdown}
        {mobileSheet}
      </div>
      <SelectErrorMessages errors={allErrorMessages} name={name} submitted={submitted} toPresentErrors={toPresentErrors} />
    </div>
  );
};

export default SelectTriggerMode;
